import { useLayoutEffect, useState } from "react";
import "./dashboard.css";
import { Button, Layout, Spin } from "antd";
import {
  ArrowRightOutlined,
  DollarCircleOutlined,
  CarryOutOutlined,
  LogoutOutlined,
  UserOutlined,
  PhoneOutlined,
  ReloadOutlined,
  WhatsAppOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ShoppingCartOutlined,
  BankOutlined,
} from "@ant-design/icons";

import LayoutHeader from "../../inc/LayoutHeader";
import LayoutFooter from "../../inc/LayoutFooter";
import { displayMessage, getAPI, exPostAPI } from "../../../utils/common";
import { ERROR_MSG_TYPE } from "../../../constants/messages";
import { DASH_DATA_V2, VENDOR_BILL_DETAIL } from "../../../constants/api";
import { Doughnut, Bar } from "react-chartjs-2";

import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import {
  bookingStatusColors,
  bookingStatusText,
  CURRENCY,
} from "../../../constants/constent";
import LS_SERVICE from "../../../utils/localStorage";
import { useNavigate } from "react-router-dom";
import assets from "../../../utils/ImagesPath";
import VendorBills from "./DashboardWidgets";
import SmartPriceSuggestions from "./SmartPriceSuggestions";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
);

const HotelDashboard = () => {
  const { Content } = Layout;
  const [selectedWeek, setSelectedWeek] = useState("this_week");
  const navigate = useNavigate();
  const { hnxt_hotel_id } = LS_SERVICE.get("setting") || {};

  // For Greeting
  let greeting;
  const date = new Date();
  const currentTime = date.getHours();
  if (currentTime >= 0 && currentTime <= 12) {
    greeting = "Good Morning";
  } else if (currentTime > 12 && currentTime <= 18) {
    greeting = "Good Afternoon";
  } else {
    greeting = "Good Evening";
  }

  const handleWeekChange = (event) => {
    setSelectedWeek(event.target.value);
  };

  const statusMap = {
    2: "s-confirmed",
    0: "s-pending",
    3: "s-cancelled",
  };
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [dashData, setDashData] = useState({});

  useLayoutEffect(() => {
    getDashData();
  }, [selectedWeek]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await getDashData(); // Replace with your data fetching function
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setRefreshing(false);
    }
  };
  const getDashData = () => {
    setLoading(true);
    const successFn = function (data) {
      setLoading(false);
      if (data.status) {
        setDashData(data.data);
        setLoading(false);
      } else {
        setLoading(false);
      }
    };
    const errorFn = function () {
      setLoading(false);
      displayMessage(ERROR_MSG_TYPE, "Unable to get Dashboard Data");
    };
    getAPI(`${DASH_DATA_V2}${selectedWeek}`, successFn, errorFn);
  };

  const {
    gridData,
    recentBookings,
    roomStatus,
    revenueSnapshot,
    weeklyRevenue,
    dailyBookings,
    channelPerformance,
  } = dashData;

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        <main className="main-content">
          {/* Banner */}
          <section className="banner">
            <div className="banner-text">
              <h1>
                {greeting}, {LS_SERVICE.get("name")}!
              </h1>
              <b>
                <p>HOTEL ID: HNXT-{hnxt_hotel_id}</p>
              </b>
              {/* <p>
                Every effort counts and you're making it happen.
                <br />
                Keep up the momentum—you're doing great!
              </p> */}
              <Button
                disabled={loading || refreshing}
                style={{ marginRight: "5px" }}
                onClick={handleRefresh}
                loading={loading || refreshing}
              >
                <ReloadOutlined />
              </Button>
              <Button
                onClick={() => {
                  navigate("/stay-view");
                }}
              >
                All Bookings <ArrowRightOutlined />
              </Button>
            </div>
            <img
              alt={assets.fevicon}
              src={assets.fevicon}
              style={{
                height: "100px",
                opacity: 0.8,
                transform: "rotate(-15deg)",
              }}
            />
          </section>

          <Spin spinning={loading || refreshing}>
            {/* KPI Grid */}
            <section className="kpi-grid">
              <Kpi
                title="Total Revenue"
                value={CURRENCY + parseFloat(gridData?.totalRevenue ?? 0).toFixed(2)}
                icon={<DollarCircleOutlined />}
                color="bg-blue"
              />
              <Kpi
                title="Occupancy"
                value={gridData?.occupancy}
                icon={<CheckCircleOutlined />}
                color="bg-green"
              />
              <Kpi
                title="Today Check-in"
                value={gridData?.todayCheckin}
                icon={<CarryOutOutlined />}
                color="bg-orange"
              />
              <Kpi
                title="Today Check-out"
                value={gridData?.todayCheckout}
                icon={<LogoutOutlined />}
                color="bg-pink"
              />
            </section>
            <section className="kpi-grid">
              <Kpi
                title="Delayed Checkout"
                value={gridData?.delayedCheckout}
                icon={<CloseCircleOutlined />}
                color="bg-pink"
              />
              <Kpi
                title="OnGoing Booking"
                value={gridData?.onGoingBookings}
                icon={<ShoppingCartOutlined />}
                color="bg-blue"
              />
              <Kpi
                title="Cancelled Booking"
                value={gridData?.cancelledBookings}
                icon={<CloseCircleOutlined />}
                color="bg-pink"
              />
              <Kpi
                title="Payment Pending"
                value={CURRENCY + parseFloat(gridData?.paymentPending ?? 0).toFixed(2)}
                icon={<BankOutlined />}
                color="bg-orange"
              />
            </section>
            {/* Recent Bookings */}
            <section className="content-grid">
              <div className="left-col">
                <div className="card">
                  <div className="card-header">
                    <h3>Recent Bookings</h3>
                    <Button
                      style={{
                        backgroundColor: "white",
                        color: "black",
                        border: "none",
                      }}
                      onClick={() => {
                        navigate("/stay-view");
                      }}
                    >
                      View All
                    </Button>
                  </div>
                  <BookingTable
                    recentBookings={recentBookings}
                    statusMap={statusMap}
                  />
                </div>

                <DailyBookings
                  selectedWeek={selectedWeek}
                  handleWeekChange={handleWeekChange}
                  dailyBookings={dailyBookings}
                />
                <SmartPriceSuggestions />
              </div>

              <div className="right-col">
                <RoomStatus roomStatus={roomStatus} />
                <RevenueSnapshot revenueSnapshot={revenueSnapshot} />
                <WeeklyRevenue weeklyRevenue={weeklyRevenue} />
                <ChannelPerformance channelPerformance={channelPerformance} />
              </div>
            </section>
          </Spin>

          {/* Floating Buttons */}
          <FloatingButtons />
        </main>
      </Content>
      <LayoutFooter />
    </Layout>
  );
};

const Kpi = ({ title, value, icon, color }) => (
  <div className="kpi-card">
    <div className="kpi-info">
      <p>{title}</p>
      <h2>{value}</h2>
    </div>
    <div className={`kpi-icon ${color}`}>{icon}</div>
  </div>
);

const BookingTable = ({ recentBookings, statusMap }) => {
  const renderBookingRow = (e) => {
    const { guestName, roomType, checkinTime, status } = e;
    return (
      <BookingRow
        key={guestName + checkinTime} // Add a unique key for better React reconciliation
        name={guestName}
        room={roomType}
        time={checkinTime}
        status={status}
        statusMap={statusMap}
      />
    );
  };

  return (
    <table>
      <thead>
        <tr>
          <th>Guest Name</th>
          <th>Room Type</th>
          <th>Check-in</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>{recentBookings?.map(renderBookingRow)}</tbody>
    </table>
  );
};

const BookingRow = ({ name, room, time, status, statusMap }) => (
  <tr style={{ textAlign: "left" }}>
    <td>
      <UserOutlined /> {name}
    </td>
    <td>{room}</td>
    <td>{time}</td>
    <td>
      <span
        style={{ backgroundColor: bookingStatusColors[status] }}
        className={`status`}
      >
        {bookingStatusText[status]}
      </span>
    </td>
  </tr>
);

const DailyBookings = ({ selectedWeek, handleWeekChange, dailyBookings }) => {
  return (
    <div className="card">
      <div className="card-header">
        <h3>Daily Bookings</h3>
        <select
          style={{
            border: "none",
            color: "var(--grey)",
            outline: "none",
            fontSize: "13px",
          }}
          value={selectedWeek}
          onChange={handleWeekChange}
        >
          <option value="this_week">This Week</option>
          <option value="last_week">Last Week</option>
        </select>
      </div>

      <div
        className="chart-container"
        style={{ width: "auto", height: "450px" }}
      >
        {dailyBookings ? (
          <Bar
            data={dailyBookings ?? null}
            options={{
              responsive: true,
              scales: {
                x: {
                  beginAtZero: true,
                },
              },
            }}
          />
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};


const RoomStatus = ({ roomStatus }) => {
  return (
    <div className="card">
      <div className="card-header">
        <h3>Room Status ({roomStatus?.totalRooms ?? 0})</h3>
        <i className="fa-solid fa-bed" style={{ color: "var(--grey)" }}></i>
      </div>

      <div className="room-stat-list">
        {/* totalRooms */}
        <div className="room-stat-item">
          <span className="stat-label">
            <div className="dot" style={{ background: "var(--grey)" }}></div>
            Total
          </span>
          <span className="stat-count">{roomStatus?.totalRooms ?? 0}</span>
        </div>
        {/* Available */}
        <div className="room-stat-item">
          <span className="stat-label">
            <div className="dot" style={{ background: "var(--success)" }}></div>
            Available
          </span>
          <span className="stat-count">{roomStatus?.available ?? 0}</span>
        </div>

        {/* Occupied */}
        <div className="room-stat-item">
          <span className="stat-label">
            <div
              className="dot"
              style={{ background: "var(--primary-color)" }}
            ></div>
            Occupied
          </span>
          <span className="stat-count">{roomStatus?.occupied ?? 0}</span>
        </div>

        {/* Blocked */}
        <div className="room-stat-item">
          <span className="stat-label">
            <div
              className="dot"
              style={{ background: "var(--danger)" }} // or red
            ></div>
            Blocked
          </span>
          <span className="stat-count">{roomStatus?.blocked ?? 0}</span>
        </div>
      </div>
    </div>
  );
};

const RevenueSnapshot = ({ revenueSnapshot }) => {
  return (
    <div className="card">
      <div className="card-header">
        <h3>Revenue Snapshot</h3>
        <i
          className="fa-solid fa-file-invoice-dollar"
          style={{ color: "var(--grey)" }}
        ></i>
      </div>
      <div className="rev-list">
        <div className="rev-item">
          <span className="rev-label">
            <i className="fa-solid fa-money-bill-wave"></i> Cash
          </span>
          <span className="rev-value">{CURRENCY + revenueSnapshot?.cash}</span>
        </div>
        <div className="rev-item">
          <span className="rev-label">
            <i className="fa-regular fa-credit-card"></i> Card / Online /
            Prepaid
          </span>
          <span className="rev-value">
            {CURRENCY + revenueSnapshot?.cardOnline}
          </span>
        </div>
        {/* <div className="rev-item">
          <span className="rev-label">
            <i className="fa-solid fa-clock-rotate-left"></i> Pending
          </span>
          <span className="rev-value" style={{ color: "var(--warning)" }}>
            {CURRENCY + revenueSnapshot?.pending}
          </span>
        </div>
        <div className="rev-item">
          <span className="rev-label">
            <i className="fa-solid fa-percent"></i> GST
          </span>
          <span className="rev-value">{CURRENCY + revenueSnapshot?.gst}</span>
        </div> */}
      </div>
    </div>
  );
};

const WeeklyRevenue = ({ weeklyRevenue }) => {
  return (
    <div className="card">
      <div className="card-header">
        <h3>Weekly Revenue</h3>
        <i
          className="fa-solid fa-chart-pie"
          style={{ color: "var(--grey)" }}
        ></i>
      </div>
      <div className="donut-wrapper" style={{ width: "auto", height: "350px" }}>
        {weeklyRevenue ? (
          <Doughnut
            data={weeklyRevenue ?? null}
            options={{
              responsive: true,
              maintainAspectRatio: false, // Prevents maintaining aspect ratio for custom size

              plugins: {
                legend: {
                  position: "top",
                },
                tooltip: {
                  callbacks: {
                    label: function (context) {
                      return "₹" + context.raw;
                    },
                  },
                },
              },
            }}
          />
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

const ChannelPerformance = ({ channelPerformance }) => {
  return (
    <div className="card">
      <div className="card-header">
        <h3>Channel Performance</h3>
        <i
          className="fa-solid fa-chart-simple"
          style={{ color: "var(--grey)" }}
        ></i>
      </div>
      {channelPerformance?.map((e) => {
        // Assuming e?.bookings is out of 100, we scale it down to 10%
        const progress = Math.min(e?.bookings * 10, 100); // Max limit of 100% progress
        return (
          <div className="channel-item">
            <div className="channel-info">
              <span>{e?.channel}</span>
              <strong>{e?.bookings} Bookings</strong>
            </div>
            <div className="progress-bg">
              <div
                className="progress-fill"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const FloatingButtons = () => (
  <div className="floating-actions">
    <a
      href="https://wa.me/919838975227?text=Please%20your%20hotel%20name%20and%20issue%20with%20this%20message"
      className="float-btn wa-btn"
      target="_blank"
      rel="noreferrer"
    >
      <WhatsAppOutlined />
    </a>
    <a href="tel:+919838975227" className="float-btn call-btn">
      <PhoneOutlined />
    </a>
  </div>
);

export default HotelDashboard;
